/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module PositiveSecurityPolicyModule
 */

import { Component, Type } from '@angular/core';
import { PositiveSecurityPolicy } from 'object-types';

import { ObjectTypeItem } from 'ajs/modules/data-model/factories';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { L10nService } from '@vmw/ngx-vip';

import {
    AviPermissionResource,
    IPositiveSecurityPolicy,
} from 'generated-types';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';

import * as l10n from '../positive-security-policy.l10n';
import { PositiveSecurityParamsConfigItem } from './positive-security-params.config-item.factory';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Positive Security Policy feilds to be omited.
 */
type TPositiveSecurityPolicyOmitFields = 'configpb_attributes' | 'positive_security_params';

/**
 * Custom Positive Security Policy Type.
 */
type TPositiveSecurityPolicyPartial = Omit<IPositiveSecurityPolicy, TPositiveSecurityPolicyOmitFields>;

export interface IPositiveSecurityPolicyConfig extends TPositiveSecurityPolicyPartial {
    positive_security_params?: PositiveSecurityParamsConfigItem,
}

/**
 * @description
 *     Item class for Positive Security Policy.
 * @author Zhiqian Liu
 */
export class PositiveSecurityPolicyItem extends withEditChildMessageItemMixin(
    withFullModalMixin(ObjectTypeItem),
) {
    public static ajsDependencies = [
        'l10nService',
    ];

    /**
     * l10n Service for internationalization.
     */
    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'positivesecuritypolicy',
            windowElement: 'lazy-load',
            params: {
                include_name: true,
            },
            permissionName: AviPermissionResource.PERMISSION_POSITIVESECURITYPOLICY,
            objectType: PositiveSecurityPolicy,
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);

        this.showConfigWarnings = true;
    }

    /**
     * Getter for the 'enable_positive_security_rule_updates' flag.
     */
    public get positiveSecurityRuleUpdatesEnabled(): boolean {
        return this.getConfig().enable_positive_security_rule_updates;
    }
    /**
     * Setter for the 'enable_positive_security_rule_updates` flag.
     */
    public set positiveSecurityRuleUpdatesEnabled(enableRuleUpdates: boolean) {
        this.getConfig().enable_positive_security_rule_updates = enableRuleUpdates;
    }

    /**
     * Set the positive_security_params object to its default values in this.data.config.config. (Not a typo)
     */
    public setDefaultPositiveSecurityParams(): void {
        this.safeSetNewChildByField('positive_security_params');
    }

    /**
     * Import lazy-loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { PositiveSecurityPolicyModalComponent } = await import(
            /* webpackChunkName: "positive-security-policy-modal" */
            'ng/lazy-loaded-components/modals/positive-security-policy-modal/positive-security-policy-modal.component',
        );

        return PositiveSecurityPolicyModalComponent as Type<Component>;
    }

    // eslint-disable-next-line class-methods-use-this
    protected override requiredFields(): string[] {
        return [
            'positive_security_params',
        ];
    }

    protected override getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.positiveSecurityPolicyBreadcrumbTitle);
    }
}
